import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  ALL_TASKS_BRAND_PREFIX,
  TASKS_PREFIX,
  URL_PARAM_OPEN_TASK_ID,
  URL_PARAM_OPEN_TASKS,
  URL_PARAM_VIEW_ALL_BRAND_TASKS,
  URL_PARAM_VIEW_ALL_TASKS,
} from "~/pages/dashboard/task/tab-windows/utils/window-helpers";
import type { TabWindow } from "../../task.types";

type Props<T> = {
  activeWindows: TabWindow<T>[];
  tasks: T[] | undefined;
  brands?: { id: number; name: string }[];
  openTaskWindow: (task: T) => Promise<void> | void;
  openAllTasksWindow: (tasks: T[], brandId?: number, brandName?: string) => Promise<void> | void;
};

export const useWindowQueryParams = <T extends { id: number; brandId: number }>({
  activeWindows,
  tasks,
  brands,
  openTaskWindow,
  openAllTasksWindow,
}: Props<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeWindowsRef = useRef(activeWindows);
  const loadedWindowIdsRef = useRef(new Set<string>());

  useEffect(() => {
    activeWindowsRef.current = activeWindows;
    activeWindows.forEach((window) => {
      if (window.id.startsWith(TASKS_PREFIX) || window.id.startsWith(ALL_TASKS_BRAND_PREFIX)) {
        loadedWindowIdsRef.current.add(window.id);
      }
    });
  }, [activeWindows]);

  useEffect(() => {
    if (!tasks) return;

    const openTaskId = searchParams.get(URL_PARAM_OPEN_TASK_ID);
    const openTasks = searchParams.get(URL_PARAM_OPEN_TASKS);
    const viewAllTasks = searchParams.get(URL_PARAM_VIEW_ALL_TASKS) || "false"; //Set on sidebar-utils button to set searchParams viewAllTasks=true
    const viewAllBrandTasks = searchParams.get(URL_PARAM_VIEW_ALL_BRAND_TASKS);

    const idsToOpen = new Set<number>();

    if (openTasks) {
      openTasks.split(",").forEach((id) => {
        const num = Number(id);
        if (!isNaN(num)) idsToOpen.add(num);
      });
    }

    if (openTaskId) {
      const num = Number(openTaskId);
      if (!isNaN(num)) idsToOpen.add(num);
    }

    idsToOpen.forEach((id) => {
      const task = tasks.find((t) => t.id === id);
      if (task) {
        const windowId = `window-task-${task.id}`;
        if (!activeWindowsRef.current.find((w) => w.id === windowId)) {
          openTaskWindow(task);
        }
      }
    });

    if (viewAllTasks === "true") {
      const windowId = "window-all-tasks";
      if (!activeWindowsRef.current.find((w) => w.id === windowId)) {
        openAllTasksWindow(tasks);
      }
    }

    if (viewAllBrandTasks && brands) {
      const brandIdsToOpen = new Set<number>();
      viewAllBrandTasks.split(",").forEach((id) => {
        const num = Number(id);
        if (!isNaN(num)) brandIdsToOpen.add(num);
      });

      brandIdsToOpen.forEach((brandId) => {
        const windowId = `${ALL_TASKS_BRAND_PREFIX}${brandId}`;
        if (!activeWindowsRef.current.find((w) => w.id === windowId)) {
          const brandTasks = tasks.filter((t) => t.brandId === brandId);
          const brand = brands.find((b) => b.id === brandId);
          if (brandTasks.length > 0 && brand) {
            openAllTasksWindow(brandTasks, brandId, brand.name);
          }
        }
      });
    }
  }, [searchParams, tasks, brands, openTaskWindow, openAllTasksWindow]);

  useEffect(() => {
    const openTaskIds = activeWindows
      .filter((w) => w.id.startsWith(TASKS_PREFIX))
      .map((w) => w.id.replace(TASKS_PREFIX, ""))
      .join(",");

    const openBrandIds = activeWindows
      .filter((w) => w.id.startsWith(ALL_TASKS_BRAND_PREFIX))
      .map((w) => w.id.replace(ALL_TASKS_BRAND_PREFIX, ""))
      .join(",");

    setSearchParams(
      (prev) => {
        const currentOpenTasks = prev.get(URL_PARAM_OPEN_TASKS);
        const currentViewAllBrandTasks = prev.get(URL_PARAM_VIEW_ALL_BRAND_TASKS);
        const hasOpenTaskId = prev.has(URL_PARAM_OPEN_TASK_ID);
        const hasViewAllTasks = prev.has(URL_PARAM_VIEW_ALL_TASKS);

        const openTasksMatch = currentOpenTasks === (openTaskIds || null);
        const viewAllBrandTasksMatch = currentViewAllBrandTasks === (openBrandIds || null);

        if (openTasksMatch && viewAllBrandTasksMatch && !hasOpenTaskId && !hasViewAllTasks) {
          return prev;
        }

        if (currentOpenTasks && currentOpenTasks !== openTaskIds) {
          const urlTaskIds = currentOpenTasks.split(",").filter(Boolean);
          const openWindowIds = openTaskIds.split(",").filter(Boolean);

          const hasPendingTasks = urlTaskIds.some((urlId) => {
            const taskExists = tasks?.some((t) => t.id === Number(urlId));
            const windowId = `window-task-${urlId}`;
            const windowOpen = openWindowIds.includes(urlId);
            const wasEverLoaded = loadedWindowIdsRef.current.has(windowId);

            return taskExists && !windowOpen && !wasEverLoaded;
          });

          if (hasPendingTasks) {
            return prev;
          }
        }

        if (hasOpenTaskId) {
          const openTaskId = prev.get("openTaskId");
          const hasValidOpenTaskId = openTaskId && tasks?.some((t) => t.id === Number(openTaskId));
          const windowId = `window-task-${openTaskId}`;
          const wasEverLoaded = loadedWindowIdsRef.current.has(windowId);

          if (hasValidOpenTaskId && !openTaskIds.includes(openTaskId!) && !wasEverLoaded) {
            return prev;
          }
        }

        if (hasViewAllTasks && tasks && tasks.length > 0) {
          const wasEverLoaded = loadedWindowIdsRef.current.has("window-all-tasks");

          if (!activeWindows.some((w) => w.id === "window-all-tasks") && !wasEverLoaded) {
            return prev;
          }
        }

        if (currentViewAllBrandTasks && currentViewAllBrandTasks !== openBrandIds) {
          const urlBrandIds = currentViewAllBrandTasks.split(",").filter(Boolean);
          const openBrandWindowIds = openBrandIds.split(",").filter(Boolean);

          const hasPendingBrands = urlBrandIds.some((urlId) => {
            const brandHasTasks = tasks?.some((t) => t.brandId === Number(urlId));
            const windowId = `${ALL_TASKS_BRAND_PREFIX}${urlId}`;
            const windowOpen = openBrandWindowIds.includes(urlId);
            const wasEverLoaded = loadedWindowIdsRef.current.has(windowId);

            return brandHasTasks && !windowOpen && !wasEverLoaded;
          });

          if (hasPendingBrands) {
            return prev;
          }
        }

        const newParams = new URLSearchParams(prev);

        if (openTaskIds) {
          newParams.set(URL_PARAM_OPEN_TASKS, openTaskIds);
        } else {
          newParams.delete(URL_PARAM_OPEN_TASKS);
        }

        if (openBrandIds) {
          newParams.set(URL_PARAM_VIEW_ALL_BRAND_TASKS, openBrandIds);
        } else {
          newParams.delete(URL_PARAM_VIEW_ALL_BRAND_TASKS);
        }

        if (hasOpenTaskId) {
          newParams.delete(URL_PARAM_OPEN_TASK_ID);
        }

        if (hasViewAllTasks) {
          newParams.delete(URL_PARAM_VIEW_ALL_TASKS);
        }

        return newParams;
      },
      { replace: true }
    );
  }, [activeWindows, setSearchParams, tasks]);
};
